import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Check, Sparkles, Zap, Rocket, Crown } from 'lucide-react';

const PLANS = [
  { key: 'free', name: 'Free', price: '$0', period: '/forever', gens: 10, icon: Sparkles, color: '#A1A1AA', features: ['10 generations/month', '2 AI models', 'Basic templates', 'Local storage'] },
  { key: 'starter', name: 'Starter', price: '$29', period: '/month', gens: 100, icon: Zap, color: '#06B6D4', features: ['100 generations/month', 'All AI models', 'All templates', 'Brand kits', 'Batch processing'] },
  { key: 'growth', name: 'Growth', price: '$79', period: '/month', gens: 500, icon: Rocket, color: '#CDFE65', popular: true, features: ['500 generations/month', 'All AI models', 'All templates', 'Unlimited brand kits', 'Priority rendering', 'Export tools'] },
  { key: 'scale', name: 'Scale', price: '$199', period: '/month', gens: 2000, icon: Crown, color: '#7C3AED', features: ['2,000 generations/month', 'All AI models', 'All templates', 'Unlimited everything', 'API access', 'Dedicated support'] },
];

export default function BillingPage() {
  const { user, refreshUser } = useAuth();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState('');

  useEffect(() => {
    const load = async () => {
      try { setBilling(await api.getBilling()); } catch {} finally { setLoading(false); }
    };
    load();
  }, []);

  const handleUpgrade = async (plan) => {
    if (plan === user?.plan) return;
    setUpgrading(plan);
    try {
      const res = await api.createCheckout(plan);
      toast.success(res.message || `Upgraded to ${plan}!`);
      await refreshUser();
      setBilling(await api.getBilling());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upgrade failed');
    } finally {
      setUpgrading('');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CDFE65] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in-up opacity-0" data-testid="billing-page">
      <h1 className="text-3xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo' }}>Billing</h1>
      <p className="text-white/40 mb-8">Manage your plan and usage</p>

      {/* Current Plan */}
      <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-6 mb-8" data-testid="current-plan">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-mono uppercase tracking-wider text-white/40 mb-1">Current Plan</p>
            <h2 className="text-2xl font-black capitalize" style={{ fontFamily: 'Chivo' }}>{billing?.plan || 'free'}</h2>
          </div>
          <div className="text-right">
            <p className="text-xs font-mono uppercase tracking-wider text-white/40 mb-1">Usage</p>
            <p className="text-lg font-bold">{billing?.generations_used || 0}<span className="text-white/30">/{billing?.generations_limit || 10}</span></p>
          </div>
        </div>
        <div className="mt-4 h-2 bg-white/5 rounded-full overflow-hidden">
          <div className="h-full bg-[#CDFE65] rounded-full transition-all duration-500" style={{ width: `${Math.round(((billing?.generations_used || 0) / Math.max(billing?.generations_limit || 10, 1)) * 100)}%` }} />
        </div>
        <p className="text-xs text-white/20 mt-2 font-mono">
          {billing?.subscription_status === 'active' ? 'Subscription active' : 'Billing is MOCKED — upgrades apply instantly without payment'}
        </p>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="plans-grid">
        {PLANS.map(p => {
          const isCurrent = user?.plan === p.key;
          return (
            <div key={p.key} className={`relative bg-[#0F0F11] border ${p.popular ? 'border-[#CDFE65]' : 'border-white/5'} rounded-2xl p-6 card-hover`} data-testid={`plan-${p.key}`}>
              {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#CDFE65] text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">Popular</div>}
              <p.icon className="w-6 h-6 mb-3" style={{ color: p.color }} />
              <h3 className="text-lg font-bold mb-1">{p.name}</h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-3xl font-black" style={{ fontFamily: 'Chivo' }}>{p.price}</span>
                <span className="text-sm text-white/40">{p.period}</span>
              </div>
              <ul className="space-y-2 mb-6">
                {p.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-white/60">
                    <Check className="w-3.5 h-3.5 text-[#CDFE65] shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleUpgrade(p.key)}
                disabled={isCurrent || !!upgrading}
                className={`w-full text-center text-sm py-2.5 rounded-full font-bold transition-all ${isCurrent ? 'bg-white/5 text-white/30 cursor-default' : p.popular ? 'btn-primary' : 'btn-secondary'}`}
                data-testid={`upgrade-${p.key}-btn`}
              >
                {upgrading === p.key ? 'Upgrading...' : isCurrent ? 'Current Plan' : `Upgrade to ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
